import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router';

import logo from '../assets/brand/maklerpass-quer-gold.svg';
import { AssistantDock } from '../assistant/AssistantDock';
import { AssistantToggle } from '../assistant/AssistantPanel';
import { useMediaQuery } from '../components/useMediaQuery';
import { WelcomeTour } from '../onboarding/WelcomeTour';
import { BottomNav } from './BottomNav';
import { useAuth, useMe } from '../auth/context';
import { Icon } from '../components/icons';
import { useFieldOnly, useProject, useSupportOnly, useTelesalesOnly } from '../projects/context';
import { isRemote, projectKindDescriptions, projectKindLabels } from '../projects/kind';
import { useOrderPricePartner } from '../pricing/usePartner';
import { ActiveCallBar, IncomingCallBanner } from './CallBanners';
import { navItems } from './navigation';
import { NotificationsBell } from './NotificationsBell';

export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const { can, project } = useProject();
  const telesales = project.roles.some((r) => r.key === 'TELESALES');
  const closeNav = () => setNavOpen(false);
  // Reiner Außendienst: nur Heute (Termine mit Route) und Rückfragen – mobil, ohne Bürofunktionen.
  const fieldOnly = useFieldOnly();
  const orderPrice = useOrderPricePartner();
  const supportOnly = useSupportOnly();
  const telesalesOnly = useTelesalesOnly();
  // Außendienst auf dem Handy: Navigation unten statt Menü.
  const bottomNav = useMediaQuery('(max-width: 760px)') && fieldOnly;

  return (
    <div className={['shell', navOpen && 'nav-open', bottomNav && 'with-bottom-nav'].filter(Boolean).join(' ')}>
      <a className="skip-link" href="#main">
        Zum Inhalt springen
      </a>
      <header className="topbar">
        <button
          type="button"
          className="icon-btn nav-toggle"
          aria-label="Navigation umschalten"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((open) => !open)}
        >
          <Icon name="menu" size={22} />
        </button>
        <div className="topbar-brand">
          <img className="brand-logo" src={logo} alt="Maklerpass" />
        </div>
        <ProjectSwitcher />
        <div className="topbar-spacer" />
        <AssistantToggle open={assistantOpen} onToggle={() => setAssistantOpen((o) => !o)} />
        <NotificationsBell />
        <UserMenu />
      </header>
      <aside className="sidebar">
        <nav aria-label="Hauptnavigation">
          <ul>
            {navItems
              .filter((item) => item.visible({ can, fieldOnly, orderPrice, telesales, supportOnly, telesalesOnly }))
              .map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end} className="nav-link" onClick={closeNav}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
          </ul>
        </nav>
      </aside>
      {navOpen && <div className="backdrop" onClick={closeNav} aria-hidden="true" />}
      <main className="content" id="main" tabIndex={-1}>
        <IncomingCallBanner />
        <ActiveCallBar />
        <Outlet />
      </main>
      <AssistantDock open={assistantOpen} onOpenChange={setAssistantOpen} />
      {bottomNav && <BottomNav />}
      <WelcomeTour />
    </div>
  );
}

// Der Projektkontext ist immer sichtbar, damit niemand versehentlich im falschen Projekt arbeitet.
function ProjectSwitcher() {
  const { project, switchable, switchProject } = useProject();
  return (
    <div className="project-context">
      <span className="project-label" aria-hidden="true">
        Projekt:
      </span>
      {switchable.length <= 1 ? (
        <strong className="project-current" aria-label="Aktives Projekt">
          {project.name}
        </strong>
      ) : (
        <label className="project-switcher">
          <span className="visually-hidden">Projekt wechseln</span>
          <select value={project.key} onChange={(e) => switchProject(e.target.value)}>
            {switchable.map((p) => (
              <option key={p.id} value={p.key}>
                {isRemote(p) ? `${p.name} (${projectKindLabels[p.kind]})` : p.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {isRemote(project) && (
        <span className="badge badge-remote" title={projectKindDescriptions[project.kind]}>
          {projectKindLabels[project.kind]}
        </span>
      )}
    </div>
  );
}

function UserMenu() {
  const me = useMe();
  const { logout } = useAuth();
  const { project } = useProject();
  const roles = [...me.global_roles, ...project.roles].map((r) => r.name);
  const [busy, setBusy] = useState(false);

  return (
    <div className="user-menu">
      <Link className="user-info" to={`/p/${project.key}/profil`} title="Mein Profil">
        <span className="user-name">
          {me.user.first_name} {me.user.last_name}
        </span>
        <span className="user-roles">{[...new Set(roles)].join(', ') || 'Keine Rolle'}</span>
      </Link>
      <button
        type="button"
        className="btn btn-ghost"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void logout().finally(() => setBusy(false));
        }}
      >
        Abmelden
      </button>
    </div>
  );
}
